import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryWrapper } from 'app/types';
import { useSnakeBar } from '../../useSnakeBar';
import { toolsServices } from '../../../Domain/Tools/Services';
import type { PensioenbijMutation, PensioenbijMutationRaw } from './usePensioenbijEventsQuery';
import { normalizePensioenbijMutation } from './usePensioenbijEventsQuery';

type GetConnectionPensioenbijMutationsVariables = {
  connectionId: string;
  page?: number;
  pageSize?: number;
  status?: string[];
  searchQuery?: string;
  mutationType?: string;
};

type GetConnectionPensioenbijMutationsResultData = {
  data: PensioenbijMutation[];
  totalItems: number;
};

const queryFn =
  ({ connectionId, page, pageSize, status, searchQuery, mutationType }: GetConnectionPensioenbijMutationsVariables) =>
  async () => {
    const response = await toolsServices.getConnectionPensioenbijMutations({
      connectionId,
      page,
      pageSize,
      status,
      searchQuery,
      mutationType,
    });
    return response.data;
  };

export const ConnectionPensioenbijMutationsQueryKey = 'getConnectionPensioenbijMutations';

export const useConnectionPensioenbijMutationsQuery: UseQueryWrapper<
  GetConnectionPensioenbijMutationsResultData,
  GetConnectionPensioenbijMutationsVariables
> = ({ variables, options }) => {
  const { showErrorSnakeBar } = useSnakeBar();
  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: ConnectionPensioenbijMutationsQueryKey });
  }, [showErrorSnakeBar]);

  const select = React.useCallback((data: { 'hydra:member': PensioenbijMutationRaw[]; 'hydra:totalItems': number }) => {
    return {
      data: (data['hydra:member'] ?? []).map(normalizePensioenbijMutation),
      totalItems: data['hydra:totalItems'] ?? 0,
    };
  }, []);

  return useQuery<any>(
    [ConnectionPensioenbijMutationsQueryKey, ...Object.values(variables || {})],
    queryFn(variables),
    {
      select,
      onError,
      enabled: !!variables?.connectionId,
      ...options,
    },
  );
};
